import { useCallback, useEffect, useRef, useState } from 'react';
import { subscriptionService, UsageSummary } from '../services/subscriptionService';

type Listener = (summary: UsageSummary | null) => void;

let cachedSummary: UsageSummary | null = null;
let inflight: Promise<UsageSummary | null> | null = null;
let cachedAt = 0;
const listeners = new Set<Listener>();
const TTL_MS = 30_000;

const notify = () => listeners.forEach(fn => fn(cachedSummary));

const fetchSummary = async (force = false): Promise<UsageSummary | null> => {
  const fresh = cachedSummary && Date.now() - cachedAt < TTL_MS;
  if (!force && fresh) return cachedSummary;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const resp = await subscriptionService.getUsage();
      if (resp.success && resp.data) {
        cachedSummary = resp.data;
        cachedAt = Date.now();
        notify();
        return cachedSummary;
      }
      return cachedSummary;
    } catch {
      return cachedSummary;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
};

export const refreshUsage = () => fetchSummary(true);

export const useUsage = () => {
  const [summary, setSummary] = useState<UsageSummary | null>(cachedSummary);
  const [isLoading, setIsLoading] = useState<boolean>(!cachedSummary);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const listener: Listener = (s) => {
      if (mounted.current) setSummary(s);
    };
    listeners.add(listener);
    (async () => {
      const s = await fetchSummary();
      if (mounted.current) {
        setSummary(s);
        setIsLoading(false);
      }
    })();
    return () => {
      mounted.current = false;
      listeners.delete(listener);
    };
  }, []);

  const reload = useCallback(async () => {
    setIsLoading(true);
    await fetchSummary(true);
    setIsLoading(false);
  }, []);

  return { summary, isLoading, reload };
};
